/* eslint-disable jsx-a11y/alt-text */
'use client'

import { Document, Image, Page, Text, View } from '@react-pdf/renderer'
import { format } from 'date-fns'
import type { OrderItem, OrganizationMember } from '@skop-app/lib-domain/types'

import { getOrderItemDimensionsLabel } from '@/features/quote/lib/orderProducts'
import { splitItemsPerPage } from '@/utils'
import {
  formatAddress,
  formatWeight,
  invoiceNumberGenerator,
} from '@/utils/helpers'
import { OrderModalValues } from '@/types'

import { styles } from '../pdfStyles'
import { Footer } from '../PDFFooter/pdfFooter.organism'
import { Table } from '../PDFTable/pdfTable.organism'
import { PDFSignPage } from '../PDFSignPage/pdfSignPage.organism'

type PDFTransferVoucherProps = {
  order: OrderModalValues
  twoSidesSign?: boolean
  teamLogo?: string
  manager?: OrganizationMember
  hideSignInputs?: boolean
}

export const PDFTransferVoucher = ({
  order,
  twoSidesSign,
  teamLogo,
  manager,
  hideSignInputs = false,
}: PDFTransferVoucherProps) => {
  const { items, id, orderParameters } = order
  const invoiceNumber = invoiceNumberGenerator(id)

  // with this TransferVoucher model we can have 14 items on the first page and 28 per page on the other pages without the header
  const pages = splitItemsPerPage({
    items: items || [],
    firstPageItemsLength: 14,
    otherPagesItemsLength: 27,
  })

  const receiverDataNotAvailable =
    !order?.company ||
    !order?.address ||
    !order?.zipcode ||
    !order?.city ||
    !order?.SIRET

  return (
    <Document>
      {pages.map((page: any, pageIdx: number) => {
        return (
          <Page size="A4" style={styles.container} key={`page-${pageIdx}`}>
            {/* BODY */}
            <View>
              {pageIdx === 0 && (
                <>
                  {/* HEADER */}
                  <View style={styles.header}>
                    {teamLogo || order?.shop?.logo ? (
                      <View style={styles.logoContainer}>
                        <Image
                          style={styles.logoImage}
                          src={teamLogo || order?.shop?.logo}
                        />
                      </View>
                    ) : null}
                    <View>
                      <Text style={styles.title}>
                        Bon de cession de matériaux
                      </Text>
                      <Text style={styles.rowLine}>
                        Numéro de bon de cession: #{invoiceNumber}
                      </Text>
                      <Text style={styles.rowLine}>
                        Date d&apos;émission:{' '}
                        {format(
                          new Date(order?.issueDate ?? new Date()),
                          'dd/MM/yyyy',
                        )}
                      </Text>
                      <Text style={styles.rowLine}>
                        Date d&apos;expiration:{' '}
                        {format(
                          new Date(order?.expireDate ?? new Date()),
                          'dd/MM/yyyy',
                        )}
                      </Text>
                    </View>
                  </View>
                  {/* Skop number + Facturer à */}
                  <View
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      borderBottom: '1.4px solid black',
                      borderTop: '1.4px solid black',
                      paddingVertical: 12,
                    }}
                  >
                    <View style={styles.flex}>
                      <View style={{ width: '48%' }}>
                        <Text style={styles.bold}>Entre le Cédant :</Text>
                        <Text style={styles.shopDetails}>
                          {order?.shop?.organization?.name || order?.shop?.name}
                        </Text>
                        {order?.shop?.organization?.email ||
                        manager?.email ||
                        order?.shop?.contacts?.[0] ? (
                          <Text style={styles.shopDetails}>
                            {order?.shop?.organization?.email ||
                              order?.shop?.contacts?.[0] ||
                              manager?.email}
                          </Text>
                        ) : null}
                        {order?.shop?.organization?.phone ||
                        order?.shop?.phone ? (
                          <Text style={styles.shopDetails}>
                            {order?.shop?.organization?.phone ||
                              order?.shop?.phone}
                          </Text>
                        ) : null}
                        {order?.shop?.organization?.SIRET ? (
                          <Text style={styles.shopDetails}>
                            SIRET: {order?.shop?.organization?.SIRET}
                          </Text>
                        ) : null}
                        <Text style={styles.shopDetails}>
                          {formatAddress({
                            address: order?.shop?.organization?.address,
                            zipcode: order?.shop?.organization?.zipcode,
                            city: order?.shop?.organization?.city,
                            country: order?.shop?.organization?.country,
                            separatedLines: true,
                          })}
                        </Text>
                      </View>
                      <View style={{ width: '48%' }}>
                        <Text style={styles.bold}>Et le Cessionnaire :</Text>
                        {receiverDataNotAvailable ? (
                          <>
                            {order?.name ? (
                              <Text style={styles.shopDetails}>
                                {order?.name}
                              </Text>
                            ) : null}
                            {order?.email ? (
                              <Text style={styles.shopDetails}>
                                {order?.email}
                              </Text>
                            ) : null}
                            {order?.phone ? (
                              <Text style={styles.shopDetails}>
                                {order?.phone}
                              </Text>
                            ) : null}
                          </>
                        ) : (
                          <>
                            {order?.company ? (
                              <Text style={styles.shopDetails}>
                                {order?.company}
                              </Text>
                            ) : null}
                            {order?.SIRET ? (
                              <Text style={styles.shopDetails}>
                                SIRET: {order?.SIRET}
                              </Text>
                            ) : null}
                            <Text style={styles.shopDetails}>
                              {formatAddress({
                                address: order?.address,
                                zipcode: order?.zipcode,
                                city: order?.city,
                                country: order?.country,
                                separatedLines: true,
                              })}
                            </Text>
                          </>
                        )}
                      </View>
                    </View>
                    <View
                      style={{
                        ...styles.flex,
                        marginTop: 12,
                      }}
                    >
                      <View style={{ width: '48%' }}>
                        <Text style={styles.bold}>représentée par :</Text>
                        {manager?.name || order?.shop?.user?.name ? (
                          <Text style={styles.shopDetails}>
                            {manager?.name || order?.shop?.user?.name}
                          </Text>
                        ) : null}
                        {manager?.email ||
                        order?.shop?.contacts?.[0] ||
                        order?.shop?.user?.email ? (
                          <Text style={styles.shopDetails}>
                            {manager?.email ||
                              order?.shop?.contacts?.[0] ||
                              order?.shop?.user?.email}
                          </Text>
                        ) : null}
                        {order?.shop?.phone || order?.shop?.user?.phone ? (
                          <Text style={styles.shopDetails}>
                            {order?.shop?.phone || order?.shop?.user?.phone}
                          </Text>
                        ) : null}
                      </View>
                      {!receiverDataNotAvailable && (
                        <View style={{ width: '48%' }}>
                          <Text style={styles.bold}>représentée par :</Text>
                          {order?.name ? (
                            <Text style={styles.shopDetails}>
                              {order?.name}
                            </Text>
                          ) : null}
                          {order?.email ? (
                            <Text style={styles.shopDetails}>
                              {order.email}
                            </Text>
                          ) : null}
                          {order?.phone ? (
                            <Text style={styles.shopDetails}>
                              {order?.phone}
                            </Text>
                          ) : null}
                        </View>
                      )}
                    </View>
                  </View>
                  <View
                    style={{
                      marginTop: 2,
                    }}
                  >
                    <Text style={styles.shopDetails}>
                      Lieu de provenance :{' '}
                      {order?.items
                        ?.map((item) => item.provenance)
                        .filter(Boolean)
                        .join(', ') || '-'}
                    </Text>
                    <Text style={styles.shopDetails}>
                      Date collecte : {order?.pickupDate?.join(', ') || '-'}
                    </Text>
                  </View>
                </>
              )}

              {pageIdx === 0 && (
                <View
                  style={{
                    marginTop: 18,
                    marginBottom: 16,
                  }}
                >
                  <Text
                    style={{
                      ...styles.shopDetails,
                      ...styles.bold,
                    }}
                  >
                    Liste des élements cédés
                  </Text>
                  <Text style={styles.shopDetails}>
                    Le présent bon de cession vaut transfert de propriété et
                    donc de responsabilité de fin de vie pour les éléments
                    ci-dessous&nbsp;:
                  </Text>
                </View>
              )}

              {/* Table */}
              <Table.Root
                style={{
                  marginTop: 6,
                }}
              >
                {/* Table Header */}
                <Table.Header
                  type="outlineTable"
                  headers={[
                    {
                      label: 'Désignation',
                      style: { width: '50%' },
                    },
                    {
                      label: 'Qté',
                      style: { width: '15%' },
                    },
                    {
                      label: 'état',
                      style: { width: '15%' },
                    },
                    {
                      label: 'poids',
                      style: { width: '18%' },
                    },
                    {
                      label: 'poids total',
                      style: { width: '18%' },
                    },
                    {
                      label: 'Commentaire',
                      style: { width: '17%' },
                    },
                  ]}
                />

                {/* Table line */}
                <Table.Line />

                {/* Table items */}
                {page.map(
                  (
                    item: OrderItem & {
                      condition: string
                      weight: number
                      comment: string
                    },
                    idx: number,
                  ) => {
                    const dimensionsLabel = getOrderItemDimensionsLabel(item)
                    const rowItems = [
                      {
                        label: item.name
                          ? {
                              labelTitle: item.name.toString(),
                              labelSubtitle: dimensionsLabel,
                            }
                          : '-',
                        rowStyle: { width: '51%' },
                      },
                      {
                        label: item.quantity
                          ? item.quantity.toString() + ' ' + item.unit
                          : '-',
                        rowStyle: { width: '15%' },
                      },
                      {
                        label: item.condition ? item.condition.toString() : '-',
                        rowStyle: { width: '15%' },
                      },
                      {
                        label: formatWeight(item.weight),
                        rowStyle: { width: '18%' },
                      },
                      {
                        label: formatWeight(item.weight * item.quantity),
                        rowStyle: { width: '18%' },
                      },
                      {
                        label: item.comment ? item.comment.toString() : '-',
                        rowStyle: { width: '17%' },
                      },
                    ]
                    return (
                      <View key={item?.id}>
                        <Table.Row
                          type="outlineTable"
                          rowItems={rowItems}
                          lastRow={page.length - 1 === idx}
                        />
                        {pages.length - 1 === pageIdx &&
                          page.length - 1 === idx && <Table.Line />}
                      </View>
                    )
                  },
                )}
              </Table.Root>
            </View>
            <Footer
              label="Bon de cession généré par"
              currentPage={pageIdx + 1}
              totalPages={pages.length + 1}
            />
          </Page>
        )
      })}

      <PDFSignPage
        title={
          orderParameters?.transferOrderConditionsLabel ||
          'Bon pour accord de cession de matériaux'
        }
        description={
          orderParameters?.transferOrderConditions ||
          "Il est convenu que le <b>Cédant </b>déclare et garantit être propriétaire des Matériaux, ou avoir obtenu du Maître d'Ouvrage toutes les autorisations et ou accords préalables pour procéder à la cession des matériaux en vue de leur réemploi. Le <b>Cessionnaire</b> est tenue de vérifier la conformité, évaluer les performances techniques et les caractéristiques des matériaux avant toute remise en oeuvre. Il renonce à tous les recours contre le Cédant.<br>Le transfert de propriété des matériaux au bénéfice du Cessionnaire intervient à compter de leur chargement dans le cas d'un don, et jusqu'au paiement du prix en cas de vente. Le défaut de paiement autorisera le Cédant a exiger la restitution des matériaux aux frais et risques du Cessionnaire.<br><br>Par la signature de ce présent document, le cessionnaire s'engage à réemployer les éléments dans <b>un</b> <b>délai maximal de 2 ans </b>et assurera le stockage dans des conditions compatibles à son réemploi. Est considéré comme réemploi l'utilisation d'un matériau de nouveau pour un usage identique à celui pour lequel il avait été conçu <b>(l'article L541-1-1 du Code de l'environnement)</b>."
        }
        order={order}
        manager={manager}
        twoSidesSign={twoSidesSign}
        signPage={!hideSignInputs}
        currentPage={pages.length + 1}
        totalPages={pages.length + 1}
      />
    </Document>
  )
}
